import { useEffect, useState } from "react";
import { Loader2, Check, X, Search, MoreVertical, KeyRound, Power, Settings2, Lock, UserCog, Trash2, Pencil, Download, FileDown } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/auth-context";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  suspended: "bg-slate-100 text-slate-700 border-slate-200",
  locked: "bg-rose-100 text-rose-800 border-rose-300",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [subDialog, setSubDialog] = useState(null);
  const [tempPwd, setTempPwd] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [roleFilter, statusFilter]);

  const setStatus = async (user, status) => {
    try {
      const { data } = await api.put(`/admin/users/${user.id}/status`, { status });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? data : u)));
      toast.success(`User ${status}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const resetPassword = async (user) => {
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`, {});
      setTempPwd({ email: user.email, password: data.temporary_password });
      toast.success("Password reset; share temp credentials with user");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const forceLogout = async (user) => {
    try {
      await api.post(`/admin/users/${user.id}/force-logout`);
      toast.success("User forced to logout");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const overrideSub = async (user, payload) => {
    try {
      const { data } = await api.put(`/admin/users/${user.id}/subscription`, payload);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? data : u)));
      setSubDialog(null);
      toast.success("Subscription updated");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const changeRole = async (user, role) => {
    try {
      const { data } = await api.put(`/admin/users/${user.id}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? data : u)));
      toast.success(`Role changed to ${role}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async (payload) => {
    try {
      const { data } = await api.put(`/admin/users/${editDialog.id}`, payload);
      setUsers((prev) => prev.map((u) => (u.id === data.id ? data : u)));
      setEditDialog(null);
      toast.success("User updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteDialog.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== deleteDialog.id));
      const parts = [];
      if (data.cases_deleted) parts.push(`${data.cases_deleted} cases deleted`);
      if (data.cases_unclaimed) parts.push(`${data.cases_unclaimed} cases returned to marketplace`);
      if (data.documents_purged) parts.push(`${data.documents_purged} documents purged`);
      if (data.saved_cases_deleted) parts.push(`${data.saved_cases_deleted} saved entries cleared`);
      toast.success(parts.length ? `User deleted — ${parts.join(", ")}` : "User deleted");
      setDeleteDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const downloadFile = async (path, filename) => {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async (entity) => {
    try {
      await downloadFile(`/admin/export/${entity}.csv`, `lexintake-${entity}.csv`);
      toast.success(`${entity} exported`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const fullBackup = async (includeFiles) => {
    setBackingUp(true);
    try {
      await downloadFile(
        `/admin/backup.zip?include_files=${includeFiles ? "true" : "false"}`,
        `lexintake-backup.zip`,
      );
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally {
      setBackingUp(false);
    }
  };

  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      u.email?.toLowerCase().includes(s) ||
      u.name?.toLowerCase().includes(s) ||
      u.firm_name?.toLowerCase().includes(s) ||
      u.bar_number?.toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid="admin-users" className="p-6 lg:p-10 max-w-7xl">
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
        Admin
      </p>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F]">
          Users
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="export-users-csv-btn"
            variant="outline"
            size="sm"
            onClick={() => exportCsv("users")}
            className="rounded-lg border-slate-300"
          >
            <FileDown className="w-4 h-4 mr-2" /> Users CSV
          </Button>
          <Button
            data-testid="export-cases-csv-btn"
            variant="outline"
            size="sm"
            onClick={() => exportCsv("cases")}
            className="rounded-lg border-slate-300"
          >
            <FileDown className="w-4 h-4 mr-2" /> Cases CSV
          </Button>
          <Button
            data-testid="export-audit-csv-btn"
            variant="outline"
            size="sm"
            onClick={() => exportCsv("audit_logs")}
            className="rounded-lg border-slate-300"
          >
            <FileDown className="w-4 h-4 mr-2" /> Audit CSV
          </Button>
          <Button
            data-testid="full-backup-zip-btn"
            size="sm"
            onClick={() => fullBackup(true)}
            disabled={backingUp}
            className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
          >
            {backingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Full backup (.zip)</>}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="users-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, firm, bar #"
            className="pl-10 rounded-lg border-slate-300"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="users-role-filter" className="rounded-lg border-slate-300 sm:w-48">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="attorney">Attorneys</SelectItem>
            <SelectItem value="law_firm">Law firms</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="users-status-filter" className="rounded-lg border-slate-300 sm:w-48">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-slate-500" data-testid="users-empty">
            No users match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.18em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Subscription</th>
                  <th className="text-left px-4 py-3">Bar #</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0A192F]">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                      {u.firm_name && (
                        <p className="text-xs text-slate-500">{u.firm_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize text-slate-700">
                      {u.role.replace("_", " ")}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${
                          STATUS_COLORS[u.status] || "bg-slate-100"
                        }`}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {u.subscription_plan ? (
                        <>
                          <span className="capitalize">{u.subscription_plan}</span>{" "}
                          <span className="text-xs text-slate-500">({u.subscription_status})</span>
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {u.bar_number || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.role !== "admin" && (
                        <div className="inline-flex gap-2">
                          {u.status !== "approved" && (
                            <Button
                              size="sm"
                              data-testid={`approve-${u.id}-btn`}
                              onClick={() => setStatus(u, "approved")}
                              className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg h-8"
                            >
                              <Check className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                          )}
                          {u.status !== "rejected" && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`reject-${u.id}-btn`}
                              onClick={() => setStatus(u, "rejected")}
                              className="rounded-lg h-8 border-slate-300"
                            >
                              <X className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`actions-${u.id}-btn`}
                                className="rounded-lg h-8 px-2"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem
                                data-testid={`suspend-${u.id}-btn`}
                                onClick={() => setStatus(u, u.status === "suspended" ? "approved" : "suspended")}
                              >
                                <Power className="w-4 h-4 mr-2" />
                                {u.status === "suspended" ? "Unsuspend" : "Suspend account"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`lock-${u.id}-btn`}
                                onClick={() => setStatus(u, u.status === "locked" ? "approved" : "locked")}
                              >
                                <Lock className="w-4 h-4 mr-2" />
                                {u.status === "locked" ? "Unlock account" : "Lock account"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`force-logout-${u.id}-btn`}
                                onClick={() => forceLogout(u)}
                              >
                                <Power className="w-4 h-4 mr-2" /> Force logout
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`reset-pw-${u.id}-btn`}
                                onClick={() => resetPassword(u)}
                              >
                                <KeyRound className="w-4 h-4 mr-2" /> Reset password
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`role-attorney-${u.id}-btn`}
                                onClick={() => changeRole(u, "attorney")}
                                disabled={u.role === "attorney"}
                              >
                                <UserCog className="w-4 h-4 mr-2" /> Set as Attorney
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`role-law-firm-${u.id}-btn`}
                                onClick={() => changeRole(u, "law_firm")}
                                disabled={u.role === "law_firm"}
                              >
                                <UserCog className="w-4 h-4 mr-2" /> Set as Law Firm
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`role-client-${u.id}-btn`}
                                onClick={() => changeRole(u, "client")}
                                disabled={u.role === "client"}
                              >
                                <UserCog className="w-4 h-4 mr-2" /> Set as Client
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`sub-override-${u.id}-btn`}
                                onClick={() => setSubDialog(u)}
                              >
                                <Settings2 className="w-4 h-4 mr-2" /> Manage subscription
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`edit-user-${u.id}-btn`}
                                onClick={() => setEditDialog(u)}
                              >
                                <Pencil className="w-4 h-4 mr-2" /> Edit profile
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`delete-user-${u.id}-btn`}
                                onClick={() => setDeleteDialog(u)}
                                className="text-rose-600 focus:text-rose-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete user…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Subscription override dialog */}
      <Dialog open={!!subDialog} onOpenChange={(o) => !o && setSubDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#0A192F]">
              Manage subscription
            </DialogTitle>
          </DialogHeader>
          {subDialog && (
            <SubscriptionForm
              user={subDialog}
              onSave={(payload) => overrideSub(subDialog, payload)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Temp password dialog */}
      <Dialog open={!!tempPwd} onOpenChange={(o) => !o && setTempPwd(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#0A192F]">
              Temporary password
            </DialogTitle>
          </DialogHeader>
          {tempPwd && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Share this temporary password with the user. They should
                change it after logging in.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-sm">
                {tempPwd.email}
              </div>
              <div
                data-testid="temp-password-value"
                className="bg-[#0A192F] text-[#C5A059] rounded-lg p-3 font-mono"
              >
                {tempPwd.password}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="temp-password-close-btn"
              onClick={() => setTempPwd(null)}
              className="bg-[#0A192F] hover:bg-[#112A46]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user profile dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#0A192F]">
              Edit user profile
            </DialogTitle>
            <DialogDescription>
              Update the user&apos;s profile details. Role, status, password,
              and subscription have their own actions.
            </DialogDescription>
          </DialogHeader>
          {editDialog && (
            <EditUserForm user={editDialog} onSave={saveEdit} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete cascade confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => !o && !deleting && setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-rose-700">
              Delete this user?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 leading-relaxed">
              This permanently deletes <strong>{deleteDialog?.email}</strong> and
              cascades to their data:
            </DialogDescription>
          </DialogHeader>
          {deleteDialog && (
            <ul className="mt-1 space-y-1 text-sm text-slate-700 list-disc pl-5">
              {deleteDialog.role === "client" && (
                <>
                  <li>All of their submitted cases are deleted.</li>
                  <li>Uploaded documents are purged from object storage.</li>
                  <li>Case status history and saved-case references are removed.</li>
                </>
              )}
              {(deleteDialog.role === "attorney" || deleteDialog.role === "law_firm") && (
                <>
                  <li>Cases they had claimed are <strong>unclaimed and returned to the marketplace</strong>.</li>
                  <li>Their saved cases and monthly view counters are deleted.</li>
                </>
              )}
              <li>All sessions and password-reset tokens are revoked.</li>
              <li>This action is irreversible.</li>
            </ul>
          )}
          <DialogFooter className="gap-2">
            <Button
              data-testid="delete-cancel-btn"
              variant="outline"
              onClick={() => setDeleteDialog(null)}
              disabled={deleting}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              data-testid="delete-confirm-btn"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" /> Yes, delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubscriptionForm({ user, onSave }) {
  const [status, setStatus] = useState(user.subscription_status || "active");
  const [plan, setPlan] = useState(user.subscription_plan || "professional");
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500 mb-2">
          Status
        </p>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="sub-status-select" className="rounded-lg border-slate-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trialing">Trialing</SelectItem>
            <SelectItem value="past_due">Past due</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="incomplete">Incomplete</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500 mb-2">
          Plan
        </p>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger data-testid="sub-plan-select" className="rounded-lg border-slate-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        data-testid="sub-save-btn"
        onClick={() => onSave({ subscription_status: status, subscription_plan: plan })}
        className="w-full bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
      >
        Save changes
      </Button>
    </div>
  );
}

function EditUserForm({ user, onSave }) {
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    website: user.website || "",
    firm_name: user.firm_name || "",
    bar_number: user.bar_number || "",
    states_licensed: (user.states_licensed || []).join(", "),
    practice_areas: (user.practice_areas || []).join(", "),
  });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = () => {
    const splitList = (s) =>
      s.split(",").map((x) => x.trim()).filter(Boolean);
    onSave({
      name: form.name || null,
      phone: form.phone || null,
      website: form.website || null,
      firm_name: form.firm_name || null,
      bar_number: form.bar_number || null,
      states_licensed: form.states_licensed ? splitList(form.states_licensed) : null,
      practice_areas: form.practice_areas ? splitList(form.practice_areas) : null,
    });
  };
  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <Field label="Name">
        <Input data-testid="edit-name" value={form.name} onChange={onChange("name")} />
      </Field>
      <Field label="Phone">
        <Input data-testid="edit-phone" value={form.phone} onChange={onChange("phone")} />
      </Field>
      <Field label="Website">
        <Input data-testid="edit-website" value={form.website} onChange={onChange("website")} />
      </Field>
      <Field label="Firm name">
        <Input data-testid="edit-firm-name" value={form.firm_name} onChange={onChange("firm_name")} />
      </Field>
      <Field label="Bar number">
        <Input data-testid="edit-bar-number" value={form.bar_number} onChange={onChange("bar_number")} />
      </Field>
      <Field label="States licensed (comma separated)">
        <Input data-testid="edit-states" value={form.states_licensed} onChange={onChange("states_licensed")} />
      </Field>
      <Field label="Practice areas (comma separated)">
        <Input data-testid="edit-practice-areas" value={form.practice_areas} onChange={onChange("practice_areas")} />
      </Field>
      <Button
        data-testid="edit-user-save-btn"
        onClick={submit}
        className="w-full bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg"
      >
        Save changes
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500 mb-1">
        {label}
      </p>
      {children}
    </div>
  );
}

